import ShiftReportService from "@/components/dashboard/shiftReports/ShiftReportService";
import ErrorHandling from "@/components/ErrorHandling";
import i18n from "@/i18n";
import TileService from "@/components/dashboard/tiles/TileService";
import { toIsoDateTime } from "@/store/TimeUtils";
import store from "@/store";
import { DateTime } from "luxon";
import { mapActions } from "vuex";

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

export const SHIFT_REPORT_LOADING_STATE_ERROR = "ERROR";
export const SHIFT_REPORT_LOADING_STATE_FETCHING = "FETCHING";
export const SHIFT_REPORT_LOADING_STATE_IN_PROGRESS = "IN_PROGRESS";
export const SHIFT_REPORT_LOADING_STATE_IN_COMPLETED = "COMPLETED";
export const SHIFT_REPORT_LOADING_STATE_AWAITING_PARAMS = "AWAITING_PARAMS";

export default {
  namespaced: true,
  name: "endOfShiftReports",
  state: {
    shiftReports: null,
    isShiftReportsDialogOpen: false,
    shiftReportLoadingState: SHIFT_REPORT_LOADING_STATE_AWAITING_PARAMS,

    isShiftReportsAndMetricsLoading: false,
    lastShiftReportsAndMetricsQueryTimeMillis: 0,
  },
  getters: {
    shiftReports(state) {
      return state.shiftReports;
    },
    isShiftReportsDialogOpen(state) {
      return state.isShiftReportsDialogOpen;
    },
    shiftReportLoadingState(state) {
      return state.shiftReportLoadingState;
    },
  },
  mutations: {
    setShiftReports(state, shiftReports) {
      state.shiftReports = shiftReports;
    },
    setIsShiftReportsDialogOpen(state, flag) {
      state.isShiftReportsDialogOpen = flag;
    },
    setShiftReportLoadingState(state, shiftReportLoadingState) {
      state.shiftReportLoadingState = shiftReportLoadingState;
    },
    setShiftReportsAndMetricsLoading(state, value) {
      state.isShiftReportsAndMetricsLoading = value;
    },
    setLastShiftReportsAndMetricsQueryTimeMillis(state, value) {
      state.lastShiftReportsAndMetricsQueryTimeMillis = value;
    },
  },
  actions: {
    ...mapActions("alerts", ["fetchAlertInstances"]),
    clearWorkShiftReports({ commit }) {
      commit("setShiftReports", []);
    },
    reloadLatestShiftReports: ({ dispatch, commit }) => {
      commit("setShiftReports", []);
      const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
      const nowUtcMs = DateTime.now().toMillis();
      //add one week to now just in case of synchronization issues.
      dispatch("fetchShiftReports", { queryStartTime: nowUtcMs - oneWeekMs, queryEndTime: nowUtcMs + oneWeekMs });
    },
    /**
     * Fetches shift reports for the current time frame
     */
    fetchShiftReports({ commit, state }, { queryStartTime, queryEndTime }) {
      if (store.getters["user/isPresenter"]) {
        return;
      }
      if (state.isShiftReportsAndMetricsLoading) return;

      const nowMillis = DateTime.now().toMillis();
      if (nowMillis - state.lastShiftReportsAndMetricsQueryTimeMillis < 5000) {
        return;
      }

      commit("setShiftReportsAndMetricsLoading", true);
      commit("setShiftReportLoadingState", SHIFT_REPORT_LOADING_STATE_FETCHING);
      // Since shift reports require the previous shift's timeframe, we need to fetch metrics before the first shift report
      const factoryId = store.getters["navigation/activeFactory"]?.id;
      const users = store.getters["users/users"];
      const productionUnit = store.getters["dashboard/activeProductionUnit"];

      if (!factoryId || !productionUnit) {
        commit("setShiftReportLoadingState", SHIFT_REPORT_LOADING_STATE_AWAITING_PARAMS);
        return;
      }
      const oneDayBefore = queryStartTime - ONE_DAY_MS;
      commit("setShiftReportLoadingState", SHIFT_REPORT_LOADING_STATE_IN_PROGRESS);
      ShiftReportService.get(productionUnit.id, toIsoDateTime(queryStartTime))
        .then((response) => {
          const workShiftReportResponse = response;
          const workShiftCoverage = store.getters["dashboard/workShiftCoverage"];
          const rawWorkShiftReports = getSortedRawWorkShiftReports(workShiftReportResponse.data);

          transformToShiftReport({
            rawWorkShiftReports: rawWorkShiftReports,
            workShiftCoverage: workShiftCoverage,
            productionUnit: productionUnit,
            queryEndTime: queryEndTime,
            users: users,
            commit: commit,
            queryStartTime: oneDayBefore,
          });

          commit("setShiftReportLoadingState", SHIFT_REPORT_LOADING_STATE_IN_COMPLETED);

          commit("setShiftReportsAndMetricsLoading", false);
          commit("setLastShiftReportsAndMetricsQueryTimeMillis", DateTime.now().toMillis());
        })
        .catch((error) => {
          commit("setShiftReportsAndMetricsLoading", false);
          displayError(commit, error);
          console.log(error);
          commit("setShiftReportLoadingState", SHIFT_REPORT_LOADING_STATE_ERROR);
        });
    },
    create({ commit, dispatch }, { request }) {
      commit("setIsShiftReportsDialogOpen", false);
      commit("setShiftReports", []);
      ShiftReportService.create(request.productionUnitId, request.payload)
        .then(() => {
          store.dispatch("alerts/fetchAlertInstances");
          dispatch("reloadLatestShiftReports");
        })
        .catch((error) => {
          displayError(commit, error);
          console.log("error", error);
          commit("setShiftReportLoadingState", SHIFT_REPORT_LOADING_STATE_ERROR);
        });
    },
    update({ commit, dispatch }, { request }) {
      commit("setIsShiftReportsDialogOpen", false);
      commit("setShiftReports", []);
      ShiftReportService.update(request.id, request.productionUnitId, request.payload)
        .then(() => {
          dispatch("reloadLatestShiftReports");
        })
        .catch((error) => {
          displayError(commit, error);
          console.log("error", error);
          commit("setShiftReportLoadingState", SHIFT_REPORT_LOADING_STATE_ERROR);
        });
    },
  },
};

function displayError(commit, error) {
  commit(
    "operation/showOperationError",
    ErrorHandling.buildErrorsMessages(error.response, (code) => i18n.t("common.errors.default", { code: code })),
    { root: true },
  );
}
function getSortedRawWorkShiftReports(rawWorkShiftReports) {
  return rawWorkShiftReports && rawWorkShiftReports.length > 0
    ? rawWorkShiftReports.sort((a, b) => a.start_millis_utc - b.start_millis_utc)
    : [];
}
function transformToShiftReport({
  rawWorkShiftReports,
  workShiftCoverage,
  productionUnit,
  queryStartTime,
  queryEndTime,
  users,
  commit,
}) {
  TileService.getMetricsByShift(productionUnit.id, toIsoDateTime(queryStartTime), toIsoDateTime(queryEndTime))
    .then((httpResponse) => {
      if (httpResponse.status === 200) {
        const filteredCoverage = workShiftCoverage.filter((it) => it.start_millis_utc >= queryStartTime);
        const sortedWorkShiftPuMetrics = httpResponse.data.sort((a, b) => a.key.start_time - b.key.start_time);
        const reports = ShiftReportService.buildWorkShiftReport({
          rawWorkShiftReports: rawWorkShiftReports,
          workShiftCoverage: filteredCoverage,
          users: users,
          sortedWorkShiftPuMetrics: sortedWorkShiftPuMetrics,
          productionUnit: productionUnit,
        });
        commit("setShiftReports", reports);
      }
    })

    .catch((error) => {
      displayError(commit, error);
      console.log("error", error);
      commit("setShiftReportLoadingState", SHIFT_REPORT_LOADING_STATE_ERROR);
    });
}
